import type { DeferredRowUpdate } from '@/store/table-editing.store'
import type { QueryClient } from '@tanstack/react-query'
import { getCachedTaskSnapshot } from '@/lib/tasks/task-cache-snapshot'
import { tableEditingStore } from '@/store/table-editing.store'

import type { Task, TaskWithAssignments } from '@scrambl/shared'
import { isOlderRecordRevision } from '@scrambl/shared/offline'

import type { TaskReaderProjection } from './task-reader-projection'
import { createRealtimeCacheUpdater } from './RealtimeCacheUpdater'
import {
  pruneTaskReaderFields,
  taskReaderProjection,
} from './task-reader-projection'

type EditingScope = {
  key: readonly unknown[]
  enabled: boolean
  signal: AbortSignal
  assertUnchanged: () => void
}
type EditingOwner = { active: boolean }
type PendingField = { value: unknown; revision: unknown; timestamp: number }
type PendingRow = {
  fields: Map<string, PendingField>
  mirror?: DeferredRowUpdate
}

const states = new WeakMap<QueryClient, Map<string, TaskEditingState>>()

/** One queue per cache and immutable workspace session, shared by its editors. */
export function getTaskEditingState(client: QueryClient, scope: EditingScope) {
  let byScope = states.get(client)
  if (!byScope) {
    byScope = new Map()
    states.set(client, byScope)
  }
  const key = JSON.stringify(scope.key)
  for (const [oldKey, state] of byScope) {
    if (oldKey !== key && !state.isCurrent()) {
      // Scope changes may be observed during render. Clear the UI mirror
      // after render; the captured guard already rejects old queued work.
      queueMicrotask(() => state.dispose())
      byScope.delete(oldKey)
    }
  }
  let state = byScope.get(key)
  if (!state) {
    state = new TaskEditingState(client, scope)
    byScope.set(key, state)
  }
  return state
}

class TaskEditingState {
  private writes = new Map<string, Set<EditingOwner>>()
  private interactions = new Map<string, Map<string, Set<EditingOwner>>>()
  private pending = new Map<string, PendingRow>()
  private applied = new Map<string, Map<string, number>>()
  private owners = new Set<EditingOwner>()
  private editingListeners = new Set<() => void>()
  private unsubscribe?: () => void
  private interval?: ReturnType<typeof setInterval>
  private scheduled = false
  private disposed = false
  private updater: ReturnType<typeof createRealtimeCacheUpdater>

  constructor(
    private client: QueryClient,
    private scope: EditingScope,
  ) {
    this.updater = createRealtimeCacheUpdater(client, 120, () =>
      this.isCurrent(),
    )
  }

  isCurrent() {
    if (this.disposed || !this.scope.enabled || this.scope.signal.aborted)
      return false
    try {
      this.scope.assertUnchanged()
      return true
    } catch {
      return false
    }
  }

  hasAnyEditing() {
    return (
      this.isCurrent() && (this.writes.size > 0 || this.interactions.size > 0)
    )
  }

  subscribeEditing(listener: () => void) {
    this.editingListeners.add(listener)
    return () => {
      this.editingListeners.delete(listener)
    }
  }

  private notifyEditing() {
    for (const listener of this.editingListeners) listener()
  }

  retain(owner: EditingOwner) {
    if (!this.isCurrent())
      return () => {
        owner.active = false
      }
    owner.active = true
    this.owners.add(owner)
    if (!this.unsubscribe) {
      this.unsubscribe = tableEditingStore.subscribe(() => this.scheduleFlush())
      this.scope.signal.addEventListener('abort', this.handleAbort, {
        once: true,
      })
      this.interval = setInterval(() => this.cleanup(), 30000)
    }
    this.scheduleFlush()
    return () => {
      owner.active = false
      this.owners.delete(owner)
      for (const [id, owners] of this.writes) {
        owners.delete(owner)
        if (!owners.size) this.writes.delete(id)
      }
      for (const [id, fields] of this.interactions) {
        for (const [field, owners] of fields) {
          owners.delete(owner)
          if (!owners.size) fields.delete(field)
        }
        if (!fields.size) this.interactions.delete(id)
      }
      if (!this.owners.size) {
        this.stopBridge()
        for (const [id, row] of this.pending) this.clearMirror(id, row)
        this.pending.clear()
        this.applied.clear()
      }
      this.notifyEditing()
      this.scheduleFlush()
    }
  }

  private handleAbort = () => this.dispose()

  private stopBridge() {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    if (this.interval) clearInterval(this.interval)
    this.interval = undefined
    this.scope.signal.removeEventListener('abort', this.handleAbort)
  }

  dispose() {
    this.disposed = true
    this.stopBridge()
    for (const [id, row] of this.pending) this.clearMirror(id, row)
    this.pending.clear()
    this.writes.clear()
    this.interactions.clear()
    this.applied.clear()
    this.notifyEditing()
    this.editingListeners.clear()
  }

  markTaskEditing(owner: EditingOwner, id: string, editing: boolean) {
    if (!owner.active || !this.isCurrent()) return
    if (editing) {
      const owners = this.writes.get(id) ?? new Set<EditingOwner>()
      owners.add(owner)
      this.writes.set(id, owners)
    } else {
      const owners = this.writes.get(id)
      owners?.delete(owner)
      if (!owners?.size) this.writes.delete(id)
      this.scheduleFlush()
    }
    this.notifyEditing()
  }

  isTaskBeingEdited(id: string) {
    return !!this.writes.get(id)?.size || !!this.interactions.get(id)?.size
  }

  beginFieldInteraction(owner: EditingOwner, id: string, field: string) {
    if (!owner.active || !this.isCurrent()) return
    const fields =
      this.interactions.get(id) ?? new Map<string, Set<EditingOwner>>()
    const owners = fields.get(field) ?? new Set<EditingOwner>()
    owners.add(owner)
    fields.set(field, owners)
    this.interactions.set(id, fields)
    this.notifyEditing()
  }

  endFieldInteraction(owner: EditingOwner, id: string, field: string) {
    if (!owner.active || !this.isCurrent()) return null
    const fields = this.interactions.get(id)
    const owners = fields?.get(field)
    owners?.delete(owner)
    if (!owners?.size) fields?.delete(field)
    if (!fields?.size) this.interactions.delete(id)
    this.notifyEditing()
    this.scheduleFlush()
    // Closing is a peek: the control still needs to apply the queued value.
    return this.getPendingUpdate(id, field)
  }

  getPendingUpdate(id: string, field: string) {
    if (!this.isCurrent()) return null
    const pending = this.pending.get(id)?.fields.get(field)
    const current = getCachedTaskSnapshot(this.client, id)
    return pending &&
      Date.now() - pending.timestamp <= 60000 &&
      !isOlderRecordRevision(current?.revision, pending.revision)
      ? pending
      : null
  }

  getDeferredUpdates(id: string) {
    if (!this.isCurrent()) return undefined
    const fields = this.pending.get(id)?.fields
    return fields
      ? Object.fromEntries(
          [...fields].map(([field, entry]) => [field, entry.value]),
        )
      : undefined
  }

  getLastFieldApplied(id: string, field: string) {
    return this.isCurrent() ? (this.applied.get(id)?.get(field) ?? 0) : 0
  }

  reconcileReaderProjection(id: string, projection: TaskReaderProjection) {
    if (!this.isCurrent()) return
    const current = getCachedTaskSnapshot(this.client, id)
    if (
      isOlderRecordRevision(current?.revision, projection.revision) ||
      isOlderRecordRevision(
        taskReaderProjection(current)?.revision,
        projection.revision,
      )
    )
      return
    const row = this.pending.get(id)
    if (row) {
      const allowed = new Set(projection.fields)
      for (const [field, entry] of row.fields) {
        if (!allowed.has(field)) row.fields.delete(field)
        else if (field === 'customFields')
          entry.value = pruneTaskReaderFields(
            { customFields: entry.value },
            projection,
          ).customFields
      }
      this.mirror(id, row)
    }
    this.updater.redactTaskFields(id, projection)
  }

  applyTaskUpdates(id: string, updates: Partial<Task>) {
    if (!this.isCurrent()) return
    const current = getCachedTaskSnapshot(this.client, id)
    if (isOlderRecordRevision(current?.revision, updates.revision)) return
    const incomingProjection = taskReaderProjection(updates)
    if (
      Object.hasOwn(updates, '_readerProjection') &&
      (!incomingProjection ||
        isOlderRecordRevision(
          taskReaderProjection(current)?.revision,
          incomingProjection.revision,
        ))
    )
      return
    if (incomingProjection)
      this.reconcileReaderProjection(id, incomingProjection)
    const projection =
      incomingProjection ??
      taskReaderProjection(getCachedTaskSnapshot(this.client, id))
    if (projection) {
      updates = pruneTaskReaderFields(
        updates as Record<string, unknown>,
        projection,
      ) as Partial<Task>
      delete (updates as Record<string, unknown>)._readerProjection
    }
    if (
      this.isTaskBeingEdited(id) ||
      tableEditingStore.getState().hasOpenPopoverForRow(id)
    ) {
      const preview = this.getDeferredUpdates(id)
      if (isOlderRecordRevision(preview?.revision, updates.revision)) return
      const row: PendingRow = this.pending.get(id) ?? { fields: new Map() }
      for (const [field, value] of Object.entries(updates)) {
        if (value === undefined) continue
        const previous = row.fields.get(field)
        if (isOlderRecordRevision(previous?.revision, updates.revision))
          continue
        row.fields.set(field, {
          value,
          revision: updates.revision,
          timestamp: Date.now(),
        })
      }
      this.pending.set(id, row)
      this.mirror(id, row)
      return
    }
    this.applyNow(id, updates)
  }

  applyPendingNow(id: string, field: string) {
    if (
      this.writes.get(id)?.size ||
      this.interactions.get(id)?.get(field)?.size
    )
      return false
    const entry = this.getPendingUpdate(id, field)
    if (!entry) return false
    const row = this.pending.get(id)!
    row.fields.delete(field)
    this.mirror(id, row)
    this.applyNow(id, {
      [field]: entry.value,
      revision: entry.revision,
    } as Partial<Task>)
    return true
  }

  private applyNow(id: string, updates: Partial<Task>) {
    if (!this.isCurrent()) return
    const current = getCachedTaskSnapshot(this.client, id)
    if (isOlderRecordRevision(current?.revision, updates.revision)) return
    const patch = Object.fromEntries(
      Object.entries(updates).filter(([, value]) => value !== undefined),
    )
    if (!Object.keys(patch).length) return
    const cached = this.client.getQueryData<TaskWithAssignments>(['task', id])
    if (
      patch.availableStatuses == null &&
      cached?.availableStatuses !== undefined
    )
      patch.availableStatuses = cached.availableStatuses
    this.updater.updateTaskFields(id, patch as Partial<TaskWithAssignments>)
    const fields = this.applied.get(id) ?? new Map()
    for (const field of Object.keys(patch)) fields.set(field, Date.now())
    this.applied.set(id, fields)
    this.updater.invalidate(['task', id], 'none')
  }

  private clearMirror(id: string, row: PendingRow) {
    const store = tableEditingStore.getState()
    if (row.mirror && store.getDeferredUpdate(id) === row.mirror)
      store.clearDeferredUpdate(id)
    row.mirror = undefined
  }

  private mirror(id: string, row: PendingRow) {
    // Global row state is only a UI mirror. Actual values and ownership live
    // in this session's queue; never merge another session/cache's values.
    const store = tableEditingStore.getState()
    if (store.getDeferredUpdate(id)?.entityType === 'task')
      store.clearDeferredUpdate(id)
    if (!row.fields.size) {
      this.pending.delete(id)
      row.mirror = undefined
      return
    }
    store.queueDeferredUpdate('task', id, this.getDeferredUpdates(id)!)
    row.mirror = store.getDeferredUpdate(id)
  }

  private scheduleFlush() {
    if (this.scheduled || !this.owners.size || !this.pending.size) return
    this.scheduled = true
    queueMicrotask(() => {
      this.scheduled = false
      if (!this.owners.size) return
      if (!this.isCurrent()) {
        this.dispose()
        return
      }
      const store = tableEditingStore.getState()
      for (const [id, row] of this.pending) {
        if (this.isTaskBeingEdited(id) || store.hasOpenPopoverForRow(id))
          continue
        this.pending.delete(id)
        this.clearMirror(id, row)
        // Keep each field's original version. Applying a later sparse patch
        // must not relabel an earlier queued field as a newer server value.
        const groups = new Map<unknown, Record<string, unknown>>()
        for (const [field, entry] of row.fields) {
          if (Date.now() - entry.timestamp > 60000) continue
          const patch = groups.get(entry.revision) ?? {}
          patch[field] = entry.value
          groups.set(entry.revision, patch)
        }
        const ordered = [...groups].sort(([a], [b]) =>
          typeof a === 'number' && typeof b === 'number' ? a - b : 0,
        )
        for (const [revision, patch] of ordered)
          this.applyNow(id, { ...patch, revision } as Partial<Task>)
      }
    })
  }

  private cleanup() {
    if (!this.isCurrent()) {
      this.dispose()
      return
    }
    const now = Date.now()
    for (const [id, row] of this.pending) {
      for (const [field, entry] of row.fields)
        if (now - entry.timestamp > 60000) row.fields.delete(field)
      if (!row.fields.size) {
        this.pending.delete(id)
        this.clearMirror(id, row)
      }
    }
    for (const [id, fields] of this.applied) {
      for (const [field, timestamp] of fields)
        if (now - timestamp > 60000) fields.delete(field)
      if (!fields.size) this.applied.delete(id)
    }
  }
}
